/**
 * BadgeSubscribers — single fan-out component that mounts every domain's
 * sidebar badge subscriber once at app shell level. Each subscriber
 * self-gates on the user's role and renders nothing visible, so this
 * collapses to a fragment in the DOM.
 *
 * Migration model (Phases 3-5 of the badge migration plan):
 *   - One REST baseline fetch per (re)connect — no 30 s polling.
 *   - Each subscriber opens a SignalR connection to `/hubs/dashboard/<domain>`
 *     and reduces incoming `BadgeDelta` events into a zustand store.
 *   - Connection lifecycle is driven by `useDomainDashboard`: when a
 *     subscriber's role gate flips false the SDK hook tears the
 *     connection down via the cleanup effect; on logout the same cleanup
 *     runs because the whole subtree unmounts.
 *
 * Mount this once inside `MainLayout` (or any layout that sits inside
 * the auth guard but outside the per-domain routes).
 */

import { useEffect } from 'react';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { HRBadgeSubscriber } from './HRBadgeSubscriber';
import { LeaveApprovalBadgeSubscriber } from './LeaveApprovalBadgeSubscriber';
import { ProcurementBadgeSubscriber } from './ProcurementBadgeSubscriber';
import { ProjectBadgeSubscriber } from './ProjectBadgeSubscriber';
import { ProjectOriginSubscriber } from './ProjectOriginSubscriber';
import { ResourceBadgeSubscriber } from './ResourceBadgeSubscriber';
import { TaskBadgeSubscriber } from './TaskBadgeSubscriber';
import { AnnouncementBadgeSubscriber } from './AnnouncementBadgeSubscriber';
import { BookingBadgeSubscriber } from './BookingBadgeSubscriber';
import { BudgetBadgeSubscriber } from './BudgetBadgeSubscriber';
import { AssetBadgeSubscriber } from './AssetBadgeSubscriber';
import { VendorBadgeSubscriber } from './VendorBadgeSubscriber';
import { UserBadgeSubscriber } from './UserBadgeSubscriber';
import { StoreBadgeSubscriber } from './StoreBadgeSubscriber';

const DEBUG = import.meta.env.DEV;

export function BadgeSubscribers() {
  const { user } = useEnrichedAuth();

  useEffect(() => {
    if (!DEBUG) return;
    // eslint-disable-next-line no-console
    console.log('[BadgeSubscribers] mounted', {
      isAuthed: !!user,
      userId: user?.id,
      roles: user?.roles ?? [],
    });
  }, [user]);

  // Defensive: skip mounting any subscriber until the user is loaded.
  // Avoids transient unauthenticated WS handshakes on app boot.
  if (!user) return null;

  return (
    <>
      <HRBadgeSubscriber />
      <LeaveApprovalBadgeSubscriber />
      <ProcurementBadgeSubscriber />
      <ProjectBadgeSubscriber />
      <ProjectOriginSubscriber />
      <ResourceBadgeSubscriber />
      <TaskBadgeSubscriber />
      <AnnouncementBadgeSubscriber />
      <BookingBadgeSubscriber />
      <BudgetBadgeSubscriber />
      <AssetBadgeSubscriber />
      <VendorBadgeSubscriber />
      <UserBadgeSubscriber />
      <StoreBadgeSubscriber />
    </>
  );
}
